/**
 * Org context: current user's organization and role (from GET /api/org/me).
 * Used for Sidebar (org name + role above logout) and Settings Team Members.
 */

import { createContext, useContext, useCallback, useState, useEffect } from 'react'
import { orgAPI } from '../services/api'
import { useAuth } from './AuthContext'

const OrgContext = createContext(null)

export function OrgProvider({ children }) {
  const { isAuthenticated } = useAuth()
  const [org, setOrg] = useState(null)
  const [role, setRole] = useState(null)
  const [allOrgs, setAllOrgs] = useState([])
  const [loading, setLoading] = useState(true)
  // True once /org/me has answered (or failed) for the signed-in session. Right after sign-in there is one render
  // where loading is still false from the signed-out state, so a role check must wait for this, not just !loading.
  const [resolved, setResolved] = useState(false)
  // Why the last lookup failed ({ status, code }), or null when it answered. Lets a page say the organization could not be
  // loaded instead of treating "no org" as "member".
  const [error, setError] = useState(null)

  const fetchOrgMe = useCallback(async () => {
    if (!isAuthenticated) {
      setOrg(null)
      setRole(null)
      setAllOrgs([])
      setLoading(false)
      setResolved(false)
      setError(null)
      return
    }
    setLoading(true)
    try {
      const [meRes, listRes] = await Promise.all([
        orgAPI.getMe(),
        orgAPI.listOrgs(),
      ])
      const meData = meRes?.data
      setOrg(meData?.org ?? null)
      setRole(meData?.role ?? null)
      setAllOrgs(listRes?.data?.orgs ?? [])
      setError(null)
    } catch (err) {
      // Only clear org state on a true session-invalid 401 (token gone /
      // expired / revoked / missing). A 403 means "you can't see this org's
      // data" — which can happen mid-session for legitimate reasons (e.g.
      // org membership changed, viewing a meeting from a different org) and
      // must NOT wipe the user's primary org context. A bare 401 from a
      // non-session endpoint is also kept because the same defensive guard
      // exists in api.ts. For all other errors (network, 5xx) keep state so
      // transient failures don't blank the dashboard.
      const status = err?.status ?? err?.response?.status
      const code = err?.code ?? err?.body?.code ?? err?.body?.error?.code
      setError({ status: status ?? null, code: code ?? null })
      const SESSION_INVALID_CODES = new Set([
        'INVALID_TOKEN', // authMiddleware: the bearer failed verification
        'TOKEN_INVALID',
        'TOKEN_EXPIRED',
        'TOKEN_REVOKED',
        'AUTH_REQUIRED',
        'SESSION_EXPIRED',
      ])
      const isSessionInvalid =
        status === 401 && (
          (code != null && SESSION_INVALID_CODES.has(code)) ||
          /authentication required|session expired|invalid token|token expired/i.test(err?.message ?? '')
        )
      if (isSessionInvalid) {
        setOrg(null)
        setRole(null)
        setAllOrgs([])
      }
      // else: leave existing state intact
    } finally {
      setLoading(false)
      setResolved(true)
    }
  }, [isAuthenticated])

  useEffect(() => {
    fetchOrgMe()
  }, [fetchOrgMe])

  const switchOrg = useCallback(async (orgId) => {
    await orgAPI.switchOrg(orgId)
    await fetchOrgMe()
  }, [fetchOrgMe])

  const value = {
    org,
    role,
    allOrgs,
    loading,
    resolved,
    error,
    // Same roles the API accepts as org admin (agentsRouter isOrgAdmin): an owner must see the admin pages it may use.
    isAdmin: role === 'org_admin' || role === 'admin' || role === 'owner',
    isPersonalOrg: org?.isPersonal === true,
    refetchOrg: fetchOrgMe,
    switchOrg,
  }

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  )
}

export function useOrg() {
  const context = useContext(OrgContext)
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider')
  }
  return context
}
